import React, { useState } from 'react';
import axios from 'axios';
import { Network, ChevronDown, ChevronUp, Loader2, AlertTriangle, Search, Clock } from 'lucide-react';
import API_BASE_URL from '../apiConfig';

const EXAMPLES = [
  "Which business types are underserved in my territory?",
  "What archetypes have I been visiting most in the last 90 days?",
  "Which areas have the highest competition saturation?",
  "Which business types do I tend to avoid?",
  "Show me entities similar to a high-priority lead in my area",
];

function ResultsTable({ results }) {
  if (!results || results.length === 0) {
    return (
      <p className="text-sm text-gray-400 italic py-4 text-center">
        No results found for that query.
      </p>
    );
  }

  // Array of objects → table
  if (typeof results[0] === 'object' && results[0] !== null) {
    const keys = Object.keys(results[0]);
    return (
      <div className="overflow-x-auto rounded-lg border border-gray-700">
        <table className="w-full text-sm text-left">
          <thead className="bg-gray-700/60">
            <tr>
              {keys.map(k => (
                <th key={k} className="px-4 py-2 text-xs font-semibold text-gray-300 uppercase tracking-wider whitespace-nowrap">
                  {k.replace(/_/g, ' ')}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {results.map((row, i) => (
              <tr key={i} className={`border-t border-gray-700/50 ${i % 2 === 0 ? 'bg-gray-800/40' : 'bg-gray-800/20'} hover:bg-gray-700/40 transition-colors`}>
                {keys.map(k => {
                  const val = row[k];
                  const display = val === null || val === undefined ? '—' : String(val);
                  return (
                    <td key={k} className="px-4 py-2.5 text-gray-200 whitespace-nowrap max-w-xs truncate" title={display}>
                      {display}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  // Scalar values → list
  return (
    <ul className="space-y-1">
      {results.map((v, i) => (
        <li key={i} className="text-sm text-gray-200 py-1 px-3 bg-gray-800/40 rounded border border-gray-700/50">
          {String(v)}
        </li>
      ))}
    </ul>
  );
}

function CypherBlock({ cypher, open, onToggle }) {
  if (!cypher) return null;
  return (
    <div className="mt-4 rounded-lg border border-gray-700 overflow-hidden">
      <button
        onClick={onToggle}
        className="w-full flex items-center justify-between px-4 py-2.5 bg-gray-800/60 hover:bg-gray-700/60 text-xs text-gray-400 font-medium transition-colors"
      >
        <span className="flex items-center gap-2">
          <Network size={12} className="text-blue-400" />
          Generated Cypher
        </span>
        {open ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
      </button>
      {open && (
        <pre className="p-4 text-xs font-mono text-emerald-300 bg-gray-900 overflow-x-auto whitespace-pre-wrap leading-relaxed">
          {cypher}
        </pre>
      )}
    </div>
  );
}

export default function GraphQueryPanel({ currentUser }) {
  const [question, setQuestion] = useState('');
  const [loading, setLoading] = useState(false);
  const [response, setResponse] = useState(null);
  const [cypherOpen, setCypherOpen] = useState(false);
  const [lastQuestion, setLastQuestion] = useState('');

  const submit = async (q) => {
    const text = (q || question).trim();
    if (!text || loading) return;
    setLoading(true);
    setResponse(null);
    setCypherOpen(false);
    setLastQuestion(text);
    try {
      const res = await axios.post(`${API_BASE_URL}/api/graph/query`, { question: text });
      setResponse(res.data);
    } catch (err) {
      const detail = err.response?.data?.detail;
      setResponse({ success: false, error: typeof detail === 'string' ? detail : 'Request failed. Check your connection.' });
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
      submit();
    }
  };

  const applyExample = (ex) => {
    setQuestion(ex);
    submit(ex);
  };

  const resultCount = response?.results?.length ?? null;

  return (
    <div className="flex flex-col h-full bg-gray-900 overflow-y-auto">

      {/* Header */}
      <div className="flex-shrink-0 px-6 pt-6 pb-4 border-b border-gray-800">
        <div className="flex items-center gap-3 mb-1">
          <div className="w-8 h-8 rounded-lg bg-blue-600/20 flex items-center justify-center flex-shrink-0">
            <Network size={16} className="text-blue-400" />
          </div>
          <h1 className="text-lg font-semibold text-white">Territory Intelligence</h1>
        </div>
        <p className="text-sm text-gray-400 ml-11">
          Ask anything about your territory in plain English. The graph answers in real time.
        </p>
      </div>

      {/* Query area */}
      <div className="flex-shrink-0 px-6 py-5 border-b border-gray-800">

        {/* Example chips */}
        <div className="mb-3">
          <p className="text-xs text-gray-500 font-medium uppercase tracking-wider mb-2">Try asking</p>
          <div className="flex flex-wrap gap-2">
            {EXAMPLES.map((ex) => (
              <button
                key={ex}
                onClick={() => applyExample(ex)}
                disabled={loading}
                className="text-xs px-3 py-1.5 rounded-full border border-gray-700 text-gray-400
                           hover:border-blue-500 hover:text-blue-300 hover:bg-blue-900/20
                           disabled:opacity-40 disabled:cursor-not-allowed transition-all duration-150"
              >
                {ex}
              </button>
            ))}
          </div>
        </div>

        {/* Input */}
        <div className="flex flex-col gap-2">
          <div className="relative">
            <Search size={15} className="absolute left-3 top-3 text-gray-500 pointer-events-none" />
            <textarea
              value={question}
              onChange={(e) => setQuestion(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Which business types are underserved in my territory?"
              disabled={loading}
              rows={2}
              className="w-full pl-9 pr-4 py-2.5 bg-gray-800 border border-gray-700 rounded-lg
                         text-sm text-white placeholder-gray-500 resize-none
                         focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500/30
                         disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            />
          </div>
          <div className="flex items-center justify-between">
            <span className="text-xs text-gray-600">⌘↵ to submit</span>
            <button
              onClick={() => submit()}
              disabled={!question.trim() || loading}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-500
                         disabled:bg-gray-700 disabled:text-gray-500 disabled:cursor-not-allowed
                         text-white text-sm font-medium rounded-lg transition-colors"
            >
              {loading ? (
                <>
                  <Loader2 size={14} className="animate-spin" />
                  Thinking…
                </>
              ) : (
                <>
                  <Network size={14} />
                  Ask Intelligence
                </>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Results area */}
      <div className="flex-1 px-6 py-5">

        {/* Loading skeleton */}
        {loading && (
          <div className="space-y-3 animate-pulse">
            <div className="h-3 bg-gray-800 rounded w-1/3" />
            <div className="h-10 bg-gray-800 rounded" />
            <div className="h-8 bg-gray-800 rounded w-5/6" />
            <div className="h-8 bg-gray-800 rounded w-4/6" />
          </div>
        )}

        {/* No result yet + not loading */}
        {!loading && !response && (
          <div className="flex flex-col items-center justify-center py-16 text-center">
            <div className="w-12 h-12 rounded-full bg-gray-800 flex items-center justify-center mb-4">
              <Network size={22} className="text-gray-600" />
            </div>
            <p className="text-sm text-gray-500 max-w-xs">
              Select an example above or type your own question to query the territory knowledge graph.
            </p>
          </div>
        )}

        {/* Guard blocked */}
        {!loading && response && !response.is_safe && (
          <div className="rounded-lg border border-yellow-700/50 bg-yellow-900/20 p-4">
            <div className="flex items-start gap-3">
              <AlertTriangle size={16} className="text-yellow-400 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-yellow-300">Query not allowed</p>
                <p className="text-xs text-yellow-400/80 mt-1">
                  {response.guard_reason || 'The generated query did not pass the safety check.'}
                </p>
                <p className="text-xs text-gray-500 mt-2">
                  Try rephrasing your question as a read-only data lookup.
                </p>
              </div>
            </div>
            <CypherBlock cypher={response.cypher} open={cypherOpen} onToggle={() => setCypherOpen(v => !v)} />
          </div>
        )}

        {/* Error */}
        {!loading && response?.error && response.is_safe !== false && (
          <div className="rounded-lg border border-red-800/50 bg-red-900/20 p-4">
            <div className="flex items-start gap-3">
              <AlertTriangle size={16} className="text-red-400 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-red-300">Something went wrong</p>
                <p className="text-xs text-red-400/80 mt-1">{response.error}</p>
              </div>
            </div>
            <CypherBlock cypher={response.cypher} open={cypherOpen} onToggle={() => setCypherOpen(v => !v)} />
          </div>
        )}

        {/* Success results */}
        {!loading && response?.is_safe && !response.error && (
          <div>
            {/* Result header */}
            <div className="flex items-center justify-between mb-3">
              <div>
                <p className="text-xs text-gray-500 italic truncate max-w-lg">"{lastQuestion}"</p>
              </div>
              <div className="flex items-center gap-3 flex-shrink-0 ml-4">
                {resultCount !== null && (
                  <span className="text-xs text-gray-500">
                    {resultCount === 0 ? 'No results' : `${resultCount} row${resultCount !== 1 ? 's' : ''}`}
                  </span>
                )}
                {response.processing_time_ms != null && (
                  <span className="flex items-center gap-1 text-xs text-gray-600">
                    <Clock size={10} />
                    {response.processing_time_ms}ms
                  </span>
                )}
              </div>
            </div>

            {response.answer && (
              <div className="mb-3 px-4 py-3 rounded-lg bg-blue-500/10 border border-blue-500/30">
                <p className="text-sm text-blue-100 leading-relaxed">{response.answer}</p>
              </div>
            )}

            <ResultsTable results={response.results} />
            <CypherBlock cypher={response.cypher} open={cypherOpen} onToggle={() => setCypherOpen(v => !v)} />
          </div>
        )}
      </div>
    </div>
  );
}
